import { EditableProTable } from '@ant-design/pro-components'
import { Button, Col, Form, Popconfirm, Row, Select, Typography, Modal, Upload } from 'antd'
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { generateUuid } from 'utils/dataUtil'
import { TableOutlined } from '@ant-design/icons'
import Papa from 'papaparse'
import TableEditor from './TableEditor'

import classes from './Table.module.scss'
import { isDecreasing, isIncreasing } from 'utils/number'
import { isNumeric } from 'utils/string'
import Chart from 'chart.js/auto'
import { groupBy, uniq } from 'lodash'
import { useQuery } from '@tanstack/react-query'
import { getReferenceList, getReferenceData } from 'service/completionOptimizer'

const units = {
  RS: {
    FIELD: '(Mscf/STB)',
    METRIC: '(m3/m3)'
  },
  PRESSURE: {
    FIELD: '(psia)',
    METRIC: '(bar)'
  },
  FVF: {
    FIELD: '(rbbl/STB)',
    METRIC: '(rm3/sm3)'
  },
  VISCOSITY: {
    FIELD: '(cP)',
    METRIC: '(cP)'
  }
}

const getOrCreateLegendList = (chart, id) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer.querySelector('ul');

  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.style.display = 'flex';
    listContainer.style.flexDirection = 'row';
    listContainer.style.margin = 0;
    listContainer.style.padding = 0;

    legendContainer.appendChild(listContainer);
  }

  return listContainer;
};

/**
 * @type {import('chart.js').Plugin} htmlLegendPlugin
 */
const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const ul = getOrCreateLegendList(chart, options.containerID);

    // Remove old legend items
    while (ul.firstChild) {
      ul.firstChild.remove();
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);

    const groupedItems = groupBy(items, 'text')

    Object.entries(groupedItems).forEach(([legendText, items]) => {
      const item = items[0]
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'pointer';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginLeft = '10px';

      li.onclick = () => {
        items.forEach(i => {
          chart.setDatasetVisibility(i.datasetIndex, !chart.isDatasetVisible(i.datasetIndex));
        })
        chart.update();
      };

      // Color box
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.borderWidth = item.lineWidth + 'px';
      boxSpan.style.display = 'inline-block';
      boxSpan.style.flexShrink = 0;
      boxSpan.style.height = '20px';
      boxSpan.style.marginRight = '10px';
      boxSpan.style.width = '20px';

      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = item.fontColor;
      textContainer.style.margin = 0;
      textContainer.style.padding = 0;
      textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      ul.appendChild(li);
    });
  }
};

const OilTable = ({ fields = [], operation }) => {
  /**
   * @type {React.MutableRefObject<Chart>} chart
   */
  const chart = useRef()
  const chartCanvas = useRef()
  const form = Form.useFormInstance()

  const simulationUnit = Form.useWatch(["GENERAL", "unit"])

  const [data, setData] = useState([])
  const [errors, setErrors] = useState([])

  const [editingData, setEditingData] = useState([])
  const [isTableEditorOpen, setIsTableEditorOpen] = useState(false)
  const tableEditorRef = useRef(null)

  const fetchData = () => {
    setData(fields.map((field, index) => ({
      ...form.getFieldValue(["PVT", "oil", field.name]),
      key: index
    })) ?? [])
  }

  useEffect(() => {
    validateData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields])

  const columns = useMemo(
    /**
     * 
     * @returns {import('@ant-design/pro-components').ProColumnType[]}
     */
    () => [
      {
        key: 'key',
        dataIndex: 'key',
        title: '#',
        editable: false,
        render: (dom, record, index) => index + 1
      },
      {
        key: 'RS',
        dataIndex: 'RS',
        title: `Rs ${ units.RS[simulationUnit] ?? '' }`,
        editable: true
      },
      {
        key: 'PRESSURE',
        dataIndex: 'PRESSURE',
        title: `Pressure ${ units.PRESSURE[simulationUnit] ?? '' }`,
        editable: true
      },
      {
        key: 'FVF',
        dataIndex: 'FVF',
        title: `FVF ${ units.FVF[simulationUnit] ?? '' }`,
        editable: true,
      },
      {
        key: 'VISCOSITY',
        dataIndex: 'VISCOSITY',
        title: `Viscosity ${ units.VISCOSITY[simulationUnit] ?? '' }`,
        editable: true,
      },
      {
        title: "Operation",
        valueType: "option",
        key: "operation",
        width: 160,
        render: (text, record, index, action) => [
          <Button
            key="editable"
            type="text"
            className="text-primary"
            onClick={() => {
              action?.startEditable?.(record.key);
            }}
          >
            Edit
          </Button>,
          <Popconfirm
            key="delete"
            title="Sure to delete?"
            onConfirm={() => {
              operation?.remove?.(index);
              const currentOilData = form.getFieldValue(["PVT", "oil"]);
              const newOilData = currentOilData.filter((_, i) => i !== index);
              form.setFieldValue(["PVT", "oil"], newOilData);
              fetchData();
            }}
          >
            <Button type="text" danger>Delete</Button>
          </Popconfirm>
        ],
      },
    ]
    , [operation, simulationUnit])

  const chartOptions = useMemo(
    /**
     * 
     * @returns {import('chart.js').ChartData}
     */
    () => {
      const labels = uniq(data.map(row => row[columns[2].dataIndex]))

      const sections = []
      const rsData = new Array(labels.length).fill(null)
      data.forEach(row => {
        const fvf = row[columns[3].dataIndex]
        const vsc = row[columns[4].dataIndex]
        const rs = row[columns[1].dataIndex]

        if (rs) {
          const ps = row[columns[2].dataIndex]
          const psIndex = labels.indexOf(ps)
          const fvfArr = new Array(psIndex + 1)
          const vscArr = new Array(psIndex + 1)

          rsData[psIndex] = rs

          sections.push({
            fvf: fvfArr.fill(null).map((item, index) => {
              if (index < psIndex) return item
              return fvf
            }),
            vsc: vscArr.fill(null).map((item, index) => {
              if (index < psIndex) return item
              return vsc
            }),
          })
          return
        }
        const lastSection = sections[sections.length - 1]
        if (!lastSection) return;

        lastSection.fvf.push(fvf)
        lastSection.vsc.push(vsc)
      })

      const datasets = sections.flatMap(section => [{
        data: section.fvf,
        fill: false,
        borderColor: 'green',
        yAxisID: columns[3].dataIndex,
        tension: 0.4,
        label: columns[3].title,
      },
      {
        data: section.vsc,
        fill: false,
        borderColor: 'blue',
        yAxisID: columns[4].dataIndex,
        tension: 0.4,
        label: columns[4].title,
      }])

      return {
        labels,
        datasets: [
          {
            data: rsData,
            fill: false,
            borderColor: 'red',
            yAxisID: columns[1].dataIndex,
            tension: 0.4,
            label: columns[1].title
          },
          ...datasets
        ]
      }
    },
    [columns, data])

  /**
   * 
   * @param {import('chart.js').ChartData} config 
   * @returns 
   */
  const updateChart = (config) => {
    if (!chartCanvas.current) return
    if (!chart.current) return

    chart.current.data.labels = config.labels
    chart.current.data.datasets = config.datasets

    chart.current.update()
  }

  useEffect(() => {
    if (chart.current) chart.current.destroy()
    chart.current = new Chart(chartCanvas.current, {
      type: 'line',
      data: {
        labels: [],
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container',
          },
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem) {
              return tooltipItem.yAxisID;
            }
          }
        },
        scales: {
          [columns[1].dataIndex]: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: columns[1].title
            }
          },
          [columns[4].dataIndex]: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: columns[4].title
            }
          },
          [columns[3].dataIndex]: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: columns[3].title
            }
          },
          x: {
            display: true,
            title: {
              display: true,
              text: columns[1].title
            }
          }
        }
      },
      plugins: [htmlLegendPlugin]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateChart(chartOptions)
  }, [chartOptions])

  const validateData = useCallback((_data = []) => {
    setErrors([])

    const _errors = []

    if (!_data.length) return;

    let sectionsCount = 0;

    const rsValues = []
    const pressureValues = []
    const fvfValues = []
    const viscosityValues = []

    _data.forEach((row, index) => {
      if (row.RS) {
        sectionsCount++
        if (!isNumeric(row.RS)) {
          _errors.push(`Value of RS in row #${ index + 1 } must be a number`)
        } else {
          const rs = parseFloat(row.RS)
          if (rs <= 0) {
            _errors.push(`Value of RS in row #${ index + 1 } must be > 0`)
          } else rsValues.push(rs)
        }
      }
      if (!isNumeric(row.PRESSURE)) {
        _errors.push(`Value of Pressure in row #${ index + 1 } must be a number`)
      } else {
        const pressure = parseFloat(row.PRESSURE)
        if (pressure <= 0) {
          _errors.push(`Value of Pressure in row #${ index + 1 } must be > 0`)
        } else pressureValues.push(pressure)
      }
      if (!isNumeric(row.FVF)) {
        _errors.push(`Value of FVF in row #${ index + 1 } must be a number`)
      } else {
        const fvf = parseFloat(row.FVF)
        if (fvf <= 0) {
          _errors.push(`Value of FVF in row #${ index + 1 } must be > 0`)
        } else fvfValues.push(fvf)
      }
      if (!isNumeric(row.VISCOSITY)) {
        _errors.push(`Value of Viscosity in row #${ index + 1 } must be a number`)
      } else {
        const viscosity = parseFloat(row.VISCOSITY)
        if (viscosity <= 0) {
          _errors.push(`Value of Viscosity in row #${ index + 1 } must be > 0`)
        } else viscosityValues.push(viscosity)
      }
    })

    if (sectionsCount < 2) {
      _errors.push('Oil table must have 2 or more sections')
    }

    if (_errors.length) {
      setErrors(_errors)
      return
    }

    const isRSIncreasing = isIncreasing(rsValues.filter(Boolean))
    if (!isRSIncreasing) {
      _errors.push(`The values of RS must be increasing`)
    }

    const isPressureIncreasing = isIncreasing(pressureValues)
    if (!isPressureIncreasing) {
      _errors.push(`The values of Pressure must be increasing`)
    }

    const isFVFDecreasing = isDecreasing(fvfValues)
    if (!isFVFDecreasing) {
      _errors.push(`The values of FVF must be decreasing`)
    }

    const isViscosityIncreasing = isIncreasing(viscosityValues)
    if (!isViscosityIncreasing) {
      _errors.push(`The values of Viscosity must be increasing`)
    }

    setErrors(_errors)
  }, [])

  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        // Check if there are any rows
        if (!result.data || result.data.length === 0) {
          alert('The CSV file is empty');
          return;
        }

        // Check for required columns
        const requiredColumns = ['RS', 'PRESSURE', 'FVF', 'VISCOSITY'];
        const fileColumns = Object.keys(result.data[0]);
        const missingColumns = requiredColumns.filter(col => !fileColumns.includes(col));

        if (missingColumns.length > 0) {
          alert(`Missing required columns: ${missingColumns.join(', ')}\nPlease ensure your CSV file has columns: RS, PRESSURE, FVF, VISCOSITY`);
          return;
        }

        // Validate data types and remove invalid rows
        const validData = result.data
          .filter(row => Object.values(row).some(value => value !== null)) // Filter out empty rows
          .filter(row => {
            // Check if all required fields are present and are numbers
            return requiredColumns.every(col => {
              const value = row[col];
              return value !== null && value !== undefined && isNumeric(value);
            });
          });

        if (validData.length === 0) {
          alert('No valid data found in the CSV file. All rows must have numeric values for RS, PRESSURE, FVF, and VISCOSITY');
          return;
        }

        // Process valid data
        const parsedData = validData.map((row, index) => ({
          ...row,
          key: generateUuid(),
          RS: parseFloat(row.RS),
          PRESSURE: parseFloat(row.PRESSURE),
          FVF: parseFloat(row.FVF),
          VISCOSITY: parseFloat(row.VISCOSITY)
        }));

        // Update the data state
        setData(parsedData);

        // Auto-save the data to form fields
        fields.forEach((field, index) => {
          if (parsedData[index]) {
            form.setFieldValue(["PVT", "oil", field.name], parsedData[index]);
          }
        });

        // Validate the data
        validateData(parsedData);
      },
      error: (error) => {
        alert(`Error reading CSV file: ${error.message}`);
      }
    });
    return false; // Prevent default upload behavior
  };

  const handleTableEditorOpen = () => {
    setEditingData(data);
    setIsTableEditorOpen(true);
  };

  const handleTableEditorCancel = () => {
    setEditingData(data);
    setIsTableEditorOpen(false);
  };

  const handleTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || [];

    const fieldCount = fields.length;
    for (let i = fieldCount - 1; i >= 0; i--) {
      operation?.remove?.(i);
    }

    currentEditingData.forEach((row) => {
      const newRow = {
        RS: row.RS,
        PRESSURE: row.PRESSURE,
        FVF: row.FVF,
        VISCOSITY: row.VISCOSITY
      };
      operation?.add?.(newRow);
    });

    setData(currentEditingData);
    setIsTableEditorOpen(false);
    fetchData();
  };

  // Add query to fetch oil references
  const { data: references } = useQuery({
    queryKey: ['references', 'pvt', 'oil'],
    queryFn: () => {
      console.log('Fetching PVT Oil references list...')
      return getReferenceList('pvt', 'oil').then(data => {
        console.log('PVT Oil references list:', data)
        return data
      })
    }
  })

  // Convert references to Select options
  const referenceOptions = useMemo(() => {
    if (!references) return []
    return references.map(ref => ({
      label: ref.name,
      value: ref.id
    }))
  }, [references])

  // Handle reference change
  const handleReferenceChange = async (value) => {
    if (!value) return

    try {
      // Get specific reference data using new API
      const referenceData = await getReferenceData('pvt', 'oil', value)
      console.log('Reference data:', referenceData)

      // Remove existing fields
      const fieldCount = fields.length
      for (let i = fieldCount - 1; i >= 0; i--) {
        operation?.remove?.(i)
      }

      // Convert object to array and filter out length property
      const dataArray = Object.entries(referenceData)
        .filter(([key]) => key !== 'length')
        .map(([_, value]) => value)

      // Add reference data rows
      dataArray.forEach(row => {
        const newRow = {
          RS: row.rs,
          PRESSURE: row.pressure,
          FVF: row.fvf,
          VISCOSITY: row.viscosity
        }
        operation?.add(newRow)
      })
    } catch (error) {
      console.error('Error loading reference data:', error)
    }
  }

  return (
    <FormSection
      title="Oil"
      extra={
        <div className="d-flex justify-content-end align-items-center gap-2">
          <Button 
            icon={<TableOutlined />}
            onClick={handleTableEditorOpen}
            title="Open Table Editor"
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleFileUpload}
          >
            <Button type="primary">Upload CSV</Button>
          </Upload>
          <Typography.Text className="text-light">Reference</Typography.Text>
          <Form.Item 
            name={['PVT', 'oil', 'reference']}
            noStyle
          >
            <Select
              style={{ minWidth: 120 }} 
              dropdownMatchSelectWidth={false}
              options={referenceOptions}
              onChange={handleReferenceChange}
              allowClear
            />
          </Form.Item>
        </div>
      }
    >
      <Row>
        <Col span={12}>
          <EditableProTable
            options={false}
            pagination={false}
            search={false}
            columns={columns}
            value={data}
            rowKey="key"
            sticky={{
              offsetHeader: 32
            }}
            className={classes.tableContainer}
            cardProps={{
              className: classes.card
            }}
            tableClassName={classes.table}
            tableExtraRender={() => (
              <ul className="px-24 text-danger">
                {errors.map(errMsg => (
                  <li key={errMsg}>
                    <Typography.Text type="danger">{errMsg}</Typography.Text>
                  </li>
                ))}
              </ul>
            )}
            editable={{
              type: 'multiple',
              actionRender: (row, config, defaultDom) => [
                defaultDom.save,
                defaultDom.cancel,
              ],
              onSave: (rowKey, data, row) => {
                const { index, isNew, ...recordData } = data
                if (isNew) {
                  operation?.add?.(recordData)
                } else {
                  form.setFieldValue(["PVT", "oil", index], recordData)
                  fetchData()
                }
              }
            }}
            recordCreatorProps={{
              record: {
                key: generateUuid(),
                isNew: true
              }
            }}
          />
        </Col>
        <Col span={12} className="pb-2">
          <div className="flex flex-col gap-4">
            <div id='legend-container'></div>
            <canvas ref={chartCanvas} className='max-h-[500px]' />
          </div>
        </Col>
      </Row>

      <Modal
        title="Table Editor"
        open={isTableEditorOpen}
        onCancel={handleTableEditorCancel}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <TableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={columns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </FormSection>
  )
}

export default OilTable